import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Typography,
    Backdrop,
    CircularProgress,
    TextField,
    Paper,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Chip,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Grid,
    IconButton,
} from "@mui/material";
import {
    NavigateNext,
    ChevronLeft,
    CheckCircle,
    Error as ErrorIcon,
    Close,
} from "@mui/icons-material";
import WhatsappCampaignSteppers from "./WhatsappCampaignSteppers";
import TemplateSelectionStep from "./campaign/TemplateSelectionStep";
import MediaUploadStep from "./campaign/MediaUploadStep";
import GroupSelectionStep from "./campaign/GroupSelectionStep";
import CampaignPreviewStep from "./campaign/CampaignPreviewStep";
import SchedulingStep from "./campaign/SchedulingStep";
import axiosInstance from "../../api/axiosInstance";
import base_url from "../../constants";
import useNotification from "../common/Notification";
import Cookies from "js-cookie";
import { WB_SX } from "../../ui/ux/uiTokens";

const WhatsappCampaign = () => {
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const notify = useNotification();

    // Step 1: Template Selection
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [templateDetails, setTemplateDetails] = useState(null);
    const [loadingTemplateDetails, setLoadingTemplateDetails] = useState(false);

    // Step 2: Media Upload
    const [headerMediaId, setHeaderMediaId] = useState("");
    const [headerMediaPreviewUrl, setHeaderMediaPreviewUrl] = useState("");
    const [headerMediaLink, setHeaderMediaLink] = useState(null);
    const [headerMediaType, setHeaderMediaType] = useState(null);

    // Step 3: Group Selection
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [parameterSource, setParameterSource] = useState("group"); // "group" or "custom"
    const [customParameters, setCustomParameters] = useState([]);

    // Step 4: Scheduling
    const [campaignName, setCampaignName] = useState("");
    const [priority, setPriority] = useState(10);
    const [scheduleDateTime, setScheduleDateTime] = useState(null);
    const [scheduleType, setScheduleType] = useState("now");

    // Response Dialog State
    const [responseDialog, setResponseDialog] = useState({
        open: false,
        responseData: null,
    });

    useEffect(() => {
        if (selectedTemplate) {
            fetchTemplateDetails();
        } else {
            setTemplateDetails(null);
        }
    }, [selectedTemplate]);

    const fetchTemplateDetails = async () => {
        // Check for both 'id' and 'template_id' to handle different response structures
        const templateId = selectedTemplate?.template_id || selectedTemplate?.id;
        if (!templateId) {
            console.error("Template ID not found:", selectedTemplate);
            return;
        }

        setLoadingTemplateDetails(true);
        try {
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/template/${templateId}/details?fields=id,name,language,category,status,components,previous_category,parameter_format`,
                // `${base_url}/whatsapp-business/templates/details-db/${templateId}`

            );
            
            // Check if response has error structure
            if (response.data?.error) {
                throw new Error(response.data.error || response.data.message || 'Failed to fetch template details');
            }
            
            // Backend returns response()->json($responseData) directly, so response.data is the template data
            if (response.data && response.data.id) {
                setTemplateDetails(response.data);
            } else {
                throw new Error('Invalid response structure');
            }
        } catch (error) {
            console.error("Error fetching template details:", error);
            setTemplateDetails(null);
            notify(
                error.response?.data?.message || 
                error.response?.data?.error || 
                error.message || 
                "Failed to fetch template details", 
                "error"
            );
        } finally {
            setLoadingTemplateDetails(false);
        }
    };

    const handleNext = () => {
        if (currentStep === 0) {
            if (!selectedTemplate) {
                notify("Please select a template", "error");
                return;
            }
        } else if (currentStep === 1) {
            // Validate media upload if needed
            const headerComponent = templateDetails?.components?.find(
                (c) => c.type === "HEADER"
            );
            if (
                headerComponent &&
                ["IMAGE", "VIDEO", "DOCUMENT"].includes(
                    headerComponent.format
                )
            ) {
                if (!headerMediaId) {
                    notify(
                        "Please upload media for the header before proceeding",
                        "error"
                    );
                    return;
                }
            }
        } else if (currentStep === 2) {
            // Validate group selection
            if (!selectedGroup) {
                notify("Please select a group", "error");
                return;
            }
        } else if (currentStep === 3) {
            // Preview step - no validation needed, just proceed
        } else if (currentStep === 4) {
            if (!campaignName || campaignName.trim() === "") {
                notify("Please enter a campaign name", "error");
                return;
            }
            if (!selectedGroup) {
                notify("Please select a group", "error");
                return;
            }
            handleCreateCampaign();
            return;
        }
        setCurrentStep((prev) => prev + 1);
    };

    const handleBack = () => {
        setCurrentStep((prev) => prev - 1);
    };

    const buildCustomParameters = () => {
        // Build custom_parameters array (same structure as components)
        const customParamsArray = [];
        
        const headerComponent = templateDetails?.components?.find(
            (c) => c.type === "HEADER"
        );
        const bodyComponent = templateDetails?.components?.find(
            (c) => c.type === "BODY"
        );

        // Determine parameter requirements
        const headerParamCount = headerComponent && headerComponent.format === "TEXT" 
            ? (headerComponent.text?.match(/\{\{(\d+)\}\}/g) || []).length 
            : 0;
        const bodyParamCount = bodyComponent 
            ? (bodyComponent.text?.match(/\{\{(\d+)\}\}/g) || []).length 
            : 0;

        // Build HEADER component if custom parameters are used
        if (headerComponent && parameterSource === "custom") {
            if (headerComponent.format === "TEXT" && headerParamCount > 0) {
                // Use custom parameters from state for header
                const headerParams = (customParameters || []).slice(0, headerParamCount);
                if (headerParams.length === headerParamCount && headerParams.every(p => p && p.trim())) {
                    customParamsArray.push({
                        type: "HEADER",
                        parameters: headerParams
                            .map((text) => ({
                                type: "text",
                                text: String(text).trim(),
                            })),
                    });
                }
            } else if (
                ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerComponent.format)
            ) {
                // Media header uses media ID
                if (headerMediaId) {
                    customParamsArray.push({
                        type: "HEADER",
                        parameters: [
                            {
                                type: headerComponent.format.toLowerCase(),
                                [headerComponent.format.toLowerCase()]: {
                                    id: headerMediaId,
                                },
                            },
                        ],
                    });
                }
            }
        }

        // Build BODY component if custom parameters are used
        if (bodyComponent && bodyParamCount > 0 && parameterSource === "custom") {
            // Use custom parameters from state for body (skip header params if any)
            const bodyParams = (customParameters || []).slice(headerParamCount, headerParamCount + bodyParamCount);
            if (bodyParams.length === bodyParamCount && bodyParams.every(p => p && p.trim())) {
                customParamsArray.push({
                    type: "BODY",
                    parameters: bodyParams
                        .map((text) => ({
                            type: "text",
                            text: String(text).trim(),
                        })),
                });
            }
        }

        return customParamsArray.length > 0 ? customParamsArray : null;
    };

    const handleCreateCampaign = async () => {
        setLoading(true);
        try {
            if (!selectedGroup) {
                notify("Please select a group", "error");
                setLoading(false);
                return;
            }

            if (!campaignName || campaignName.trim() === "") {
                notify("Please enter a campaign name", "error");
                setLoading(false);
                return;
            }

            // Get template_id from selectedTemplate
            const templateId = selectedTemplate?.template_id || selectedTemplate?.id;
            if (!templateId) {
                notify("Template ID not found", "error");
                setLoading(false);
                return;
            }

            // Prepare schedule date time
            const scheduleDateTimeValue = scheduleType === "now"
                ? new Date().toISOString().slice(0, 19).replace("T", " ")
                : scheduleDateTime;

            // Build payload according to backend validation
            const payload = {
                template_id: templateId,
                group_name: selectedGroup,
                campaign_name: campaignName.trim(),
                message_priority: priority,
                schedule_date_time: scheduleDateTimeValue,
                media_link: headerMediaLink || null,
                media_type: headerMediaType || null,
            };

            // Add custom_parameters only if parameterSource is "custom"
            if (parameterSource === "custom") {
                const customParams = buildCustomParameters();
                if (customParams && customParams.length > 0) {
                    payload.custom_parameters = customParams;
                }
            }

            notify("Creating campaign...", "info");

            // Call new endpoint
            const response = await axiosInstance.post(
                `${base_url}/whatsapp-business/phone-number/send-template-group`,
                payload
            );

            // Handle response similar to TestMessage
            if (response.data?.success) {
                // Success response
                const responseData = response.data.data || response.data;
                
                // Show detailed response dialog
                setResponseDialog({
                    open: true,
                    responseData: {
                        success: true,
                        message: response.data.message || "Messages queued successfully",
                        template_id: responseData.template_id,
                        template_name: responseData.template_name,
                        group_name: responseData.group_name,
                        total_recipients: responseData.total_recipients || 0,
                        total_inserted: responseData.total_inserted || 0,
                        failed: responseData.failed || 0,
                        campaign_name: responseData.campaign_name,
                        errors: responseData.errors || [],
                    },
                });

                // Show success notification
                notify(
                    response.data.message || "Campaign created successfully!",
                    "success"
                );

                // Reset form after successful creation (after dialog is closed)
            } else {
                // Error response
                const errorData = response.data;
                setResponseDialog({
                    open: true,
                    responseData: {
                        success: false,
                        error: errorData.error || "Failed to queue messages",
                        message: errorData.message || "An error occurred",
                        total_inserted: errorData.total_inserted || 0,
                        failed: errorData.failed || 0,
                        errors: errorData.errors || [],
                        validation_errors: errorData.errors || {}, // For validation errors
                    },
                });

                notify(
                    errorData.error || errorData.message || "Failed to create campaign",
                    "error"
                );
            }
        } catch (error) {
            console.error("Error creating campaign:", error);
            let errorMessage = "Failed to create campaign. Please try again.";
            let errorDetails = null;

            if (error.response?.data) {
                const errorData = error.response.data;
                
                // Extract error message
                if (errorData.data?.error?.error_user_msg) {
                    errorMessage = errorData.data.error.error_user_msg;
                } else if (errorData.data?.error?.message) {
                    errorMessage = errorData.data.error.message;
                } else if (errorData.error) {
                    errorMessage =
                        typeof errorData.error === "string"
                            ? errorData.error
                            : errorData.error.message ||
                              errorData.error.error_user_msg ||
                              errorMessage;
                } else if (errorData.message) {
                    errorMessage = errorData.message;
                }

                // Build error response data
                errorDetails = {
                    success: false,
                    error: errorData.error || errorMessage,
                    message: errorData.message || "An error occurred",
                    total_inserted: errorData.total_inserted || 0,
                    failed: errorData.failed || 0,
                    errors: errorData.errors || [],
                    validation_errors: errorData.errors || {}, // For validation errors (422)
                };
            } else {
                errorDetails = {
                    success: false,
                    error: errorMessage,
                    message: "Network error or server unavailable",
                    total_inserted: 0,
                    failed: 0,
                    errors: [],
                };
            }

            // Show error dialog
            setResponseDialog({
                open: true,
                responseData: errorDetails,
            });

            notify(errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleCloseResponseDialog = () => {
        setResponseDialog({ open: false, responseData: null });
        
        // Reset form if campaign was successful
        if (responseDialog.responseData?.success) {
            setCurrentStep(0);
            setSelectedTemplate(null);
            setTemplateDetails(null);
            setHeaderMediaId("");
            setHeaderMediaPreviewUrl("");
            setHeaderMediaLink(null);
            setHeaderMediaType(null);
            setSelectedGroup(null);
            setParameterSource("group");
            setCustomParameters([]);
            setCampaignName("");
            setPriority(10);
            setScheduleDateTime(null);
            setScheduleType("now");
        }
    };


    return (
        <Box
            sx={{
                height: "100%",
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                overflowX: "hidden",
                ...WB_SX.page,
            }}
        >
            <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, mb: 2 }}>
                <WhatsappCampaignSteppers currentStep={currentStep} />
            </Box>

            <Box
                sx={{
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    boxSizing: "border-box",
                    overflowX: "hidden",
                }}
            >
                {currentStep === 0 && (
                    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
                        <TemplateSelectionStep
                            onTemplateSelect={setSelectedTemplate}
                            selectedTemplate={selectedTemplate}
                        />
                    </Box>
                )}

                {currentStep === 1 && (
                    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
                        <MediaUploadStep
                            selectedTemplate={selectedTemplate}
                            templateDetails={templateDetails}
                            loadingTemplateDetails={loadingTemplateDetails}
                            onMediaUploaded={(mediaInfo) => {
                                if (typeof mediaInfo === 'object' && mediaInfo !== null) {
                                    setHeaderMediaId(mediaInfo.id || "");
                                    setHeaderMediaLink(mediaInfo.media_link || null);
                                    setHeaderMediaType(mediaInfo.media_type || null);
                                } else {
                                    // Backward compatibility: if it's just a string/ID
                                    setHeaderMediaId(mediaInfo || "");
                                    setHeaderMediaLink(null);
                                    setHeaderMediaType(null);
                                }
                            }}
                            onPreviewUrlChange={setHeaderMediaPreviewUrl}
                        />
                    </Box>
                )}

                {currentStep === 2 && (
                    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
                        <GroupSelectionStep
                            selectedGroup={selectedGroup}
                            onGroupSelect={setSelectedGroup}
                            templateDetails={templateDetails}
                            parameterSource={parameterSource}
                            onParameterSourceChange={setParameterSource}
                            customParameters={customParameters}
                            onCustomParametersChange={setCustomParameters}
                        />
                    </Box>
                )}

                {currentStep === 3 && (
                    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
                        <CampaignPreviewStep
                            selectedTemplate={selectedTemplate}
                            templateDetails={templateDetails}
                            loadingTemplateDetails={loadingTemplateDetails}
                            selectedGroup={selectedGroup}
                            parameterSource={parameterSource}
                            customParameters={customParameters}
                            headerMediaId={headerMediaId}
                            headerMediaPreviewUrl={headerMediaPreviewUrl}
                        />
                    </Box>
                )}

                {currentStep === 4 && (
                    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
                        <SchedulingStep
                            onSchedule={({ scheduleDateTime: dt, scheduleType: st }) => {
                                setScheduleDateTime(dt);
                                setScheduleType(st);
                            }}
                            campaignName={campaignName}
                            onCampaignNameChange={setCampaignName}
                            priority={priority}
                            onPriorityChange={setPriority}
                        />
                        {selectedGroup && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                Campaign will be sent to all contacts in group: <strong>{selectedGroup}</strong>
                            </Alert>
                        )}
                    </Box>
                )}
            </Box>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1.5,
                    mt: 3,
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                }}
            >
                <Button
                    disabled={currentStep === 0}
                    onClick={handleBack}
                    startIcon={<ChevronLeft />}
                    sx={WB_SX.secondaryButton}
                >
                    Back
                </Button>
                <Button
                    variant="contained"
                    onClick={handleNext}
                    endIcon={<NavigateNext />}
                    disabled={loading}
                    sx={WB_SX.primaryButton}
                >
                    {currentStep === 4 ? "Create Campaign" : "Next"}
                </Button>
            </Box>

            <Backdrop
                sx={(theme) => ({
                    color: "#fff",
                    zIndex: theme.zIndex.drawer + 1,
                })}
                open={loading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            {/* Response Details Dialog */}
            <Dialog
                open={responseDialog.open}
                onClose={handleCloseResponseDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        bgcolor: responseDialog.responseData?.success
                            ? "success.main"
                            : "error.main",
                        color: "white",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {responseDialog.responseData?.success ? (
                            <CheckCircle />
                        ) : (
                            <ErrorIcon />
                        )}
                        <Typography variant="h6">
                            {responseDialog.responseData?.success
                                ? "Campaign Created Successfully"
                                : "Failed to Create Campaign"}
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={handleCloseResponseDialog}
                        sx={{ color: "white" }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ mt: 2 }}>
                    {responseDialog.responseData && (
                        <Box>
                            {/* Main Message */}
                            <Alert
                                severity={
                                    responseDialog.responseData.success
                                        ? "success"
                                        : "error"
                                }
                                sx={{ mb: 3 }}
                                icon={
                                    responseDialog.responseData.success ? (
                                        <CheckCircle />
                                    ) : (
                                        <ErrorIcon />
                                    )
                                }
                            >
                                <Typography variant="body1" fontWeight="bold">
                                    {responseDialog.responseData.message ||
                                        responseDialog.responseData.error}
                                </Typography>
                            </Alert>

                            {/* Success Response Details */}
                            {responseDialog.responseData.success && (
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid item xs={12} sm={6}>
                                        <Paper
                                            variant="outlined"
                                            sx={{ p: 2, textAlign: "center" }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                Template Name
                                            </Typography>
                                            <Typography variant="h6" fontWeight="bold">
                                                {responseDialog.responseData.template_name ||
                                                    "N/A"}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Paper
                                            variant="outlined"
                                            sx={{ p: 2, textAlign: "center" }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                Template ID
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontFamily: "monospace" }}
                                            >
                                                {responseDialog.responseData.template_id || "N/A"}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Paper
                                            variant="outlined"
                                            sx={{ p: 2, textAlign: "center" }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                Group Name
                                            </Typography>
                                            <Typography variant="h6" fontWeight="bold">
                                                {responseDialog.responseData.group_name || "N/A"}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Paper
                                            variant="outlined"
                                            sx={{ p: 2, textAlign: "center" }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                Campaign Name
                                            </Typography>
                                            <Typography variant="h6" fontWeight="bold">
                                                {responseDialog.responseData.campaign_name || "N/A"}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Paper
                                            variant="outlined"
                                            sx={{ p: 2, textAlign: "center" }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                Total Recipients
                                            </Typography>
                                            <Typography variant="h6" color="primary">
                                                {responseDialog.responseData.total_recipients || 0}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Paper
                                            variant="outlined"
                                            sx={{ p: 2, textAlign: "center", bgcolor: "success.light" }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                Successfully Queued
                                            </Typography>
                                            <Typography variant="h6" color="success.dark">
                                                {responseDialog.responseData.total_inserted || 0}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                p: 2,
                                                textAlign: "center",
                                                bgcolor:
                                                    responseDialog.responseData.failed > 0
                                                        ? "error.light"
                                                        : "transparent",
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                Failed
                                            </Typography>
                                            <Typography
                                                variant="h6"
                                                color={
                                                    responseDialog.responseData.failed > 0
                                                        ? "error.dark"
                                                        : "text.primary"
                                                }
                                            >
                                                {responseDialog.responseData.failed || 0}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>
                            )}

                            {/* Error Response Details */}
                            {!responseDialog.responseData.success && (
                                <Box>
                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        <Grid item xs={12} sm={6}>
                                            <Paper
                                                variant="outlined"
                                                sx={{ p: 2, textAlign: "center" }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Successfully Queued
                                                </Typography>
                                                <Typography variant="h6" color="success.main">
                                                    {responseDialog.responseData.total_inserted || 0}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Paper
                                                variant="outlined"
                                                sx={{
                                                    p: 2,
                                                    textAlign: "center",
                                                    bgcolor: "error.light",
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Failed
                                                </Typography>
                                                <Typography variant="h6" color="error.dark">
                                                    {responseDialog.responseData.failed || 0}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                            {/* Errors List */}
                            {responseDialog.responseData.errors &&
                                Array.isArray(responseDialog.responseData.errors) &&
                                responseDialog.responseData.errors.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Divider sx={{ mb: 2 }} />
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                            Error Details:
                                        </Typography>
                                        <List dense>
                                            {responseDialog.responseData.errors.map(
                                                (errorItem, index) => (
                                                    <ListItem key={index}>
                                                        <ListItemIcon>
                                                            <ErrorIcon color="error" fontSize="small" />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={
                                                                typeof errorItem === "string"
                                                                    ? errorItem
                                                                    : errorItem.message ||
                                                                      errorItem.error ||
                                                                      JSON.stringify(errorItem)
                                                            }
                                                            secondary={
                                                                errorItem.phone_number ||
                                                                errorItem.recipient ||
                                                                errorItem.field
                                                            }
                                                        />
                                                    </ListItem>
                                                )
                                            )}
                                        </List>
                                    </Box>
                                )}

                            {/* Validation Errors */}
                            {responseDialog.responseData.validation_errors &&
                                typeof responseDialog.responseData.validation_errors === "object" &&
                                !Array.isArray(responseDialog.responseData.validation_errors) &&
                                Object.keys(responseDialog.responseData.validation_errors).length >
                                    0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Divider sx={{ mb: 2 }} />
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                            Validation Errors:
                                        </Typography>
                                        <List dense>
                                            {Object.entries(
                                                responseDialog.responseData.validation_errors
                                            ).map(([field, messages]) => (
                                                <ListItem key={field}>
                                                    <ListItemIcon>
                                                        <ErrorIcon color="error" fontSize="small" />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={field}
                                                        secondary={
                                                            Array.isArray(messages)
                                                                ? messages.join(", ")
                                                                : String(messages)
                                                        }
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                )}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={handleCloseResponseDialog}
                        variant="contained"
                        color={responseDialog.responseData?.success ? "success" : "error"}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default WhatsappCampaign;

